import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getPageSeo } from "../seo/pageMeta";
import { getStructuredDataForPath } from "../seo/structuredData";
import { DEFAULT_OG_IMAGE, SITE_NAME, toAbsoluteUrl } from "../seo/constants";

const upsertMeta = (attribute, key, content) => {
  if (!content) return;

  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  if (!href) return;

  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const upsertJsonLd = (id, data) => {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

export const Seo = ({
  title,
  description,
  keywords,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd,
}) => {
  const location = useLocation();
  const canonicalPath = path || location.pathname;
  const canonicalUrl = toAbsoluteUrl(canonicalPath);
  const imageUrl = ogImage.startsWith("http") ? ogImage : toAbsoluteUrl(ogImage);

  useEffect(() => {
    document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta(
      "name",
      "robots",
      noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large",
    );
    upsertMeta("name", "author", SITE_NAME);
    upsertMeta("name", "publisher", SITE_NAME);
    upsertMeta("name", "geo.region", "IN-UP");
    upsertMeta("name", "geo.placename", "Kanpur");
    upsertMeta("name", "geo.position", "26.449514;80.271431");
    upsertMeta("name", "ICBM", "26.449514, 80.271431");

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:locale", "en_IN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);

    upsertLink("canonical", canonicalUrl);

    if (jsonLd) {
      upsertJsonLd("rew-structured-data", jsonLd);
    }
  }, [
    title,
    description,
    keywords,
    canonicalUrl,
    imageUrl,
    noindex,
    jsonLd,
  ]);

  return null;
};

export const RouteSeo = () => {
  const { pathname } = useLocation();
  const pageSeo = getPageSeo(pathname);
  const jsonLd = getStructuredDataForPath(pathname);

  return <Seo {...pageSeo} jsonLd={jsonLd} />;
};

export default Seo;
